/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   StorageAccountsPage.tsx
 *     Description     :   Storage Accounts secondary dashboard page
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-27
 *     Modified        :   2026-06-14 | Conformed to the Spec 197 design blueprint —
 *                         migrated off MUI (Container/Paper/Table/Chip/Grid) onto the
 *                         canonical Tailwind+token UI kit. Recharts retained for charts.
 *     Constitution    :   v1.19.0
 *     Feature         :   storage-accounts-sme
 * =========================================================================================
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Search, HardDrive, Globe, Eye, Info } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { usePageData } from '../../../hooks/usePageData';
import { useHasAccess } from '../../../hooks/useAccess';
import { formatBytes as fmtBytesCanonical } from '@/utils/formatBytes';
import { readToken } from '../../../theme/tokens'; // chart colors only (Recharts isn't token-aware)
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { apiClient } from '../../../services/apiClient';
import { storageAccountsHelp } from '../../../data/help/storageAccountsHelp';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import type {
  AzureStorageAccountSummary,
  AzureStorageAccountsResponse,
} from '@shared/types/azureStorageAccounts';
import { formatEasternDate } from '@/utils/dateFormatter';
import { NoAccessBadge, WorkerVisibilityNotice } from '../../../components/collection/WorkerVisibility';

// ============================================================================
// Types & helpers
// ============================================================================

interface ComputedStats {
  total: number;
  totalSizeBytes: number;
  geoRedundant: number;
  withContainers: number;
  subscriptions: string[];
}

/**
 * 🛑 Was a local DECIMAL formatter (÷1e9, ÷1000) over byte counts the backend builds with
 * 1024³ — binary in, decimal out, every figure 10% high. Same defect as StoragePanel, which
 * on prod displayed 53.01 TB for 48.21 TiB of storage. Delegates to the canonical Spec 157
 * util so a storage number means the same thing on every screen.
 */
function formatBytes(bytes: number | null | string): string {
  // BIGINT from SQL Server may arrive as string or BigInt — coerce to Number
  const n = bytes === null ? 0 : Number(bytes);
  if (!n || !isFinite(n)) return '0 B';
  return fmtBytesCanonical(n);
}

function isGeoRedundant(replicationType: string | null): boolean {
  if (!replicationType) return false;
  const upper = replicationType.toUpperCase();
  return upper.includes('GRS') || upper.includes('RA-GRS') || upper.includes('RAGRS');
}

const PIE_COLORS = ['#4caf50', '#2196f3', '#ff9800', '#9c27b0', '#f44336', '#00bcd4'];

/**
 * Plain-language explainer for the Azure replication SKUs. Multipliers are the
 * data-at-rest price vs LRS (LRS = 1.0×); geo options also incur inter-region egress.
 */
const REPLICATION_EXPLAINER =
  'Replication = how many copies Azure keeps and where. ' +
  'LRS (1.0× price): 3 copies in ONE datacenter — protects against disk/rack failure only. ' +
  'ZRS (~1.25×): 3 copies across availability zones in one region — survives a zone/datacenter outage. ' +
  'GRS (~2.0×): LRS + async copy to a paired region — survives a regional outage (secondary not readable). ' +
  'RA-GRS (~2.5×): GRS with read access to the secondary. ' +
  'GZRS (~2.5×): ZRS + geo copy. RA-GZRS (~3.1×): GZRS with read access to the secondary. ' +
  'Higher tiers cost more but protect against bigger failures — match the SKU to how critical the data is.';

// ============================================================================
// Component
// ============================================================================

/** One empty list, so memos keyed on the accounts stay stable before the first load. */
const NO_ACCOUNTS: AzureStorageAccountSummary[] = [];

export const StorageAccountsPage: React.FC = () => {
  // Spec 285: the Backup/Fleet "Protection" column is backup information — backup-licensed only.
  const canSeeBackup = useHasAccess('backup');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Token-derived colors for Recharts (Recharts does not read CSS tokens, so we
  // resolve the design tokens to concrete values at render).
  const chartColors = {
    grid: readToken('--color-border', '#d4d1cd'),
    axis: readToken('--color-text-secondary', '#605e5c'),
    text: readToken('--color-text-secondary', '#605e5c'),
    tooltipBg: readToken('--color-bg-card', '#ffffff'),
    tooltipText: readToken('--color-text-primary', '#201f1e'),
  };

  const [activeTab, setActiveTab] = useState(0);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [subscriptionFilter, setSubscriptionFilter] = useState(searchParams.get('subscription') || 'all');
  const [replicationFilter, setReplicationFilter] = useState(searchParams.get('replication') || 'all');
  const [resourceGroupFilter, setResourceGroupFilter] = useState(searchParams.get('rg') || 'all');
  const [accessTierFilter, setAccessTierFilter] = useState(searchParams.get('tier') || 'all');

  // Fetch — page through the API until every account is loaded (route default is
  // only the first 50; the entity table has 54). Spec 226: through usePageData, so a refresh keeps
  // the accounts on screen and a failed one shows the Refresh badge; the error view is for a list
  // that never loaded.
  const query = useQuery({
    queryKey: ['azure-storage-accounts', 'all'],
    queryFn: async () => {
      const pageSize = 200;
      const all: AzureStorageAccountsResponse['accounts'] = [];
      let page = 1;
      let total = 0;
      do {
        const response = await apiClient.get<AzureStorageAccountsResponse>(
          `/api/azure/storage-accounts?page=${page}&pageSize=${pageSize}`,
        );
        all.push(...(response.data?.accounts ?? []));
        total = response.data?.total ?? all.length;
        page += 1;
      } while (all.length < total && page < 50);
      return all;
    },
  });
  const { data, isLoading: loading, error: loadError, refresh, isRefreshing, lastFetchedAt } = usePageData(query);
  const accounts: AzureStorageAccountSummary[] = data ?? NO_ACCOUNTS;
  const error = data ? null : loadError
    ? (loadError instanceof Error ? loadError.message : 'Failed to load storage accounts')
    : null;

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (subscriptionFilter !== 'all') params.set('subscription', subscriptionFilter);
    if (replicationFilter !== 'all') params.set('replication', replicationFilter);
    if (resourceGroupFilter !== 'all') params.set('rg', resourceGroupFilter);
    if (accessTierFilter !== 'all') params.set('tier', accessTierFilter);
    setSearchParams(params);
  }, [searchQuery, subscriptionFilter, replicationFilter, resourceGroupFilter, accessTierFilter, setSearchParams]);

  const stats = useMemo<ComputedStats>(() => {
    const totalSizeBytes = accounts.reduce((s, a) => s + Number(a.totalSizeBytes ?? 0), 0);
    const geoRedundant = accounts.filter((a) => isGeoRedundant(a.replicationType)).length;
    const withContainers = accounts.filter((a) => (a.containerCount ?? 0) > 0).length;
    const subscriptions = [...new Set(accounts.map((a) => a.subscriptionName).filter(Boolean))].sort();
    return { total: accounts.length, totalSizeBytes, geoRedundant, withContainers, subscriptions };
  }, [accounts]);

  // Spec 286 T159 item 3: accounts the collection worker cannot see (no_access_since set) and the earliest such date.
  const unseen = useMemo(() => {
    const dates = accounts.map((a) => a.noAccessSince).filter((d): d is string => d != null).sort();
    return { count: dates.length, since: dates[0] ?? null };
  }, [accounts]);

  const replicationTypes = useMemo(
    () => [...new Set(accounts.map((a) => a.replicationType).filter(Boolean) as string[])].sort(),
    [accounts],
  );
  const resourceGroups = useMemo(
    () => [...new Set(accounts.map((a) => a.resourceGroup).filter(Boolean) as string[])].sort(),
    [accounts],
  );
  const accessTiers = useMemo(
    () => [...new Set(accounts.map((a) => (a as any).accessTier).filter(Boolean) as string[])].sort(),
    [accounts],
  );

  const filteredAccounts = useMemo(() => {
    let result = accounts;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.accountName.toLowerCase().includes(q) ||
          a.subscriptionName.toLowerCase().includes(q) ||
          (a.resourceGroup?.toLowerCase().includes(q) ?? false),
      );
    }
    if (subscriptionFilter !== 'all') result = result.filter((a) => a.subscriptionName === subscriptionFilter);
    if (replicationFilter !== 'all') result = result.filter((a) => a.replicationType === replicationFilter);
    if (resourceGroupFilter !== 'all') result = result.filter((a) => a.resourceGroup === resourceGroupFilter);
    if (accessTierFilter !== 'all') result = result.filter((a) => (a as any).accessTier === accessTierFilter);
    return result;
  }, [accounts, searchQuery, subscriptionFilter, replicationFilter, resourceGroupFilter, accessTierFilter]);

  const tabAccounts = useMemo(() => {
    if (activeTab === 1) return filteredAccounts.filter((a) => isGeoRedundant(a.replicationType));
    if (activeTab === 2) return filteredAccounts.filter((a) => !isGeoRedundant(a.replicationType));
    return filteredAccounts;
  }, [filteredAccounts, activeTab]);

  const geoCount = useMemo(
    () => filteredAccounts.filter((a) => isGeoRedundant(a.replicationType)).length,
    [filteredAccounts],
  );
  const localCount = filteredAccounts.length - geoCount;

  // Chart data
  const topBySize = [...filteredAccounts]
    .sort((a, b) => (b.totalSizeBytes ?? 0) - (a.totalSizeBytes ?? 0))
    .slice(0, 10)
    // 1024³, not 1e9 — the chart sat beside a table whose sizes are binary, so a decimal
    // divisor made the bar and the row disagree by 7% for the same account.
    .map((a) => ({ name: a.accountName, sizeGb: a.totalSizeBytes ? +(Number(a.totalSizeBytes) / 1024 ** 3).toFixed(2) : 0 }));

  const replicationDist = Object.entries(
    filteredAccounts.reduce<Record<string, number>>((acc, a) => {
      const key = a.replicationType ?? 'Unknown';
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {}),
  ).map(([name, value]) => ({ name, value }));
  const replTotal = replicationDist.reduce((s, d) => s + d.value, 0);

  const columns: DataTableColumn<AzureStorageAccountSummary>[] = [
    {
      key: 'accountName',
      header: 'Account Name',
      width: '15%',
      render: (a) => (
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded bg-[var(--color-bg-subtle)]">
            <HardDrive className="h-4 w-4 text-[var(--color-accent)]" />
          </div>
          <span title={a.accountName} className="truncate font-medium text-[var(--color-text-primary)]">
            {a.accountName}
          </span>
        </div>
      ),
    },
    { key: 'subscriptionName', header: 'Subscription', width: '11%', wrap: true },
    {
      key: 'isBackupBound',
      header: 'Protection',
      width: '8%',
      render: (a) => (
        <Badge variant={a.isBackupBound ? 'success' : 'secondary'} isOutlined size="sm">
          {a.isBackupBound ? 'Backup' : 'Fleet'}
        </Badge>
      ),
    },
    {
      key: 'resourceGroup',
      header: 'Resource Group',
      width: '11%',
      wrap: true,
      render: (a) => a.resourceGroup ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    { key: 'kind', header: 'Kind', width: '6%', render: (a) => a.kind ?? <span className="text-[var(--color-text-secondary)]">—</span> },
    {
      key: 'replicationType',
      header: 'Replication',
      width: '11%',
      render: (a) =>
        a.replicationType ? (
          <div className="flex items-center gap-1.5">
            <Badge variant={isGeoRedundant(a.replicationType) ? 'success' : 'secondary'} isOutlined>
              {a.replicationType}
            </Badge>
            {isGeoRedundant(a.replicationType) && (
              <AppTooltip title="Geo-redundant">
                <Globe className="h-4 w-4 text-[var(--color-status-success)]" />
              </AppTooltip>
            )}
          </div>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'accessTier',
      header: 'Access Tier',
      width: '7%',
      render: (a) =>
        a.accessTier ? <Badge variant="secondary" isOutlined>{a.accessTier}</Badge> : <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'totalSizeBytes',
      header: 'Used Storage',
      width: '9%',
      render: (a) => (
        <AppTooltip title={a.totalSizeBytes != null ? `${a.totalSizeBytes.toLocaleString()} bytes` : 'No data collected'}>
          <span>{a.totalSizeBytes != null ? formatBytes(a.totalSizeBytes) : '—'}</span>
        </AppTooltip>
      ),
    },
    {
      key: 'containerCount',
      header: 'Containers',
      align: 'right',
      width: '7%',
      render: (a) => (a.containerCount != null ? a.containerCount.toLocaleString() : '—'),
    },
    {
      key: 'lastCollected',
      header: 'Last Collected',
      width: '8%',
      render: (a) => a.noAccessSince ? <NoAccessBadge since={a.noAccessSince} /> : (
        <span className="text-[var(--color-text-secondary)]">
          {a.lastCollected ? formatEasternDate(a.lastCollected) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '6%',
      render: (a) => (
        <AppTooltip title="View details">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/storage-accounts/${a.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </AppTooltip>
      ),
    },
  ];

  const tabs = ['All Accounts', `Geo-Redundant (${geoCount})`, `Local/ZRS (${localCount})`];

  return (
    <PageLayout
      title="Storage Accounts"
      description="ARM-discovered Azure storage accounts across all subscriptions"
      actions={<PageHelpPanel content={storageAccountsHelp} buttonSize="sm" />}
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={loadError}
    >
      {error && !loading ? (
        <ErrorState title="Failed to load storage accounts" message={error} onRetry={refresh} />
      ) : (
        <>
          <WorkerVisibilityNotice
            since={unseen.since}
            counts={[{ noun: 'storage accounts', noAccess: unseen.count, total: stats.total }]}
          />
          {/* Summary tiles — also the mobile quick-glance. Each carries an Info tooltip
              naming the number + source (Warehouse/Workspace insights pattern). */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <div className="relative h-full">
              <StatCard className="h-full" label="Total Accounts" value={stats.total} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="All ARM-discovered active storage accounts loaded on this page (dbo.storage_accounts, is_active) — the count after paging through every account, not just the first page.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Total Storage" value={formatBytes(stats.totalSizeBytes)} valueClassName="text-[var(--color-accent)]" />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Sum of measured used capacity across the loaded accounts (dbo.storage_accounts.used_gb). Accounts with no collected size contribute 0 — this is measured footprint, not a billing figure.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Geo-Redundant" value={stats.geoRedundant} valueClassName="text-[var(--color-status-success)]" />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Accounts on a geo-redundant SKU (replicationType contains GRS / RA-GRS) — they keep an async copy in a paired Azure region and survive a regional outage. The rest are LRS/ZRS (single region).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="With Containers" value={stats.withContainers} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Accounts with at least one collected blob container (containerCount > 0). A 0 means either no containers or the container inventory has not been collected yet.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for charts, filters, and the full
              storage-account table.
            </Card>
          </QuickGlance>

          <Extended>
            {/* Charts */}
            <Card className="mb-6 p-4">
              <h2 className="mb-4 text-base font-semibold text-[var(--color-text-primary)]">Storage Overview</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-lg bg-[var(--color-bg-subtle)] p-3 md:col-span-2">
                  <p className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">Top Accounts by Storage Used (GB)</p>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topBySize} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11, fill: chartColors.text }} stroke={chartColors.axis} />
                      <YAxis label={{ value: 'GB', angle: -90, position: 'insideLeft', fill: chartColors.text }} tick={{ fontSize: 11, fill: chartColors.text }} stroke={chartColors.axis} />
                      <RechartsTooltip
                        formatter={(value) => [`${Number(value).toFixed(2)} GB`, 'Used Storage']}
                        contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.grid}`, borderRadius: 8, color: chartColors.tooltipText }}
                        labelStyle={{ color: chartColors.tooltipText }}
                        itemStyle={{ color: chartColors.tooltipText }}
                      />
                      <Bar dataKey="sizeGb" name="Storage (GB)" radius={[4, 4, 0, 0]}>
                        {topBySize.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={index === 0 ? '#1565c0' : index === 1 ? '#1976d2' : '#2196f3'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="rounded-lg bg-[var(--color-bg-subtle)] p-3">
                  <div className="mb-2 flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">Replication Type Distribution</p>
                    <AppTooltip title={REPLICATION_EXPLAINER} maxWidth={360}>
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                      <Pie data={replicationDist} cx="50%" cy="45%" innerRadius={45} outerRadius={80} paddingAngle={2} dataKey="value" labelLine={false}>
                        {replicationDist.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value) => [value, 'Accounts']}
                        contentStyle={{ backgroundColor: chartColors.tooltipBg, border: `1px solid ${chartColors.grid}`, borderRadius: 8, color: chartColors.tooltipText }}
                        labelStyle={{ color: chartColors.tooltipText }}
                        itemStyle={{ color: chartColors.tooltipText }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        height={48}
                        iconType="circle"
                        formatter={(value: string) => {
                          const item = replicationDist.find((d) => d.name === value);
                          const pct = item && replTotal ? Math.round((item.value / replTotal) * 100) : 0;
                          return `${value} (${pct}%)`;
                        }}
                        wrapperStyle={{ fontSize: 12, color: chartColors.text }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>

            {/* Filters */}
            <Card className="mb-6 p-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
                <div className="md:col-span-5">
                  <Input
                    isFullWidth
                    placeholder="Search by account name, subscription, resource group..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    leftAddon={<Search className="h-4 w-4" />}
                  />
                </div>
                <div className="md:col-span-3">
                  <Select
                    isFullWidth
                    label="Subscription"
                    value={subscriptionFilter}
                    onChange={(e) => setSubscriptionFilter(e.target.value)}
                    options={[{ value: 'all', label: 'All Subscriptions' }, ...stats.subscriptions.map((s) => ({ value: s, label: s }))]}
                  />
                </div>
                <div className="md:col-span-2">
                  <Select
                    isFullWidth
                    label="Replication"
                    value={replicationFilter}
                    onChange={(e) => setReplicationFilter(e.target.value)}
                    options={[{ value: 'all', label: 'All Types' }, ...replicationTypes.map((r) => ({ value: r, label: r }))]}
                  />
                </div>
                {resourceGroups.length > 0 && (
                  <div className="md:col-span-2">
                    <Select
                      isFullWidth
                      label="Resource Group"
                      value={resourceGroupFilter}
                      onChange={(e) => setResourceGroupFilter(e.target.value)}
                      options={[{ value: 'all', label: 'All Groups' }, ...resourceGroups.map((r) => ({ value: r, label: r }))]}
                    />
                  </div>
                )}
                {accessTiers.length > 0 && (
                  <div className="md:col-span-2">
                    <Select
                      isFullWidth
                      label="Access Tier"
                      value={accessTierFilter}
                      onChange={(e) => setAccessTierFilter(e.target.value)}
                      options={[{ value: 'all', label: 'All Tiers' }, ...accessTiers.map((t) => ({ value: t, label: t }))]}
                    />
                  </div>
                )}
                <div className="text-sm text-[var(--color-text-secondary)] md:col-span-2">
                  {filteredAccounts.length} of {stats.total} accounts
                </div>
              </div>
            </Card>

            {/* Tabs */}
            <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
              {tabs.map((label, i) => (
                <button
                  key={label}
                  onClick={() => setActiveTab(i)}
                  className={clsx(
                    '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                    activeTab === i
                      ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                      : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Table */}
            <Card padding="none">
              {loading ? (
                <div className="p-4">
                  <LoadingState rows={6} />
                </div>
              ) : (
                <DataTable
                  columns={canSeeBackup ? columns : columns.filter((c) => c.key !== 'isBackupBound')}
                  rows={tabAccounts}
                  rowKey={(a) => a.id}
                  onRowClick={(a) => navigate(`/storage-accounts/${a.id}`)}
                  emptyMessage="No storage accounts found"
                />
              )}
            </Card>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default StorageAccountsPage;
